<script>
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";

const editorSettings = {};

const locals = {
    operators: {
        add: '+',
        divide: '/',
        multiply: '*',
        subtract: '-',
        average: 'AVE',
        round: 'RND',
        percentage: '%',
    }
};

const calcEditorConfig = {
    cId: "calcEditor",
    cType: "calcEditor",
    cTitle: "Calculations",
    cTemplate: `<div class="calc-editor-control">
        <div class="calc-preview-pane" v-if="calcResultsPreview.preview">
            <div v-html="calcResultsPreview.preview"></div>
        </div>
        <div v-for="(item, index) in calcItems" :key="index" class="calc-item-area">
            <control :name="'CALC_TYPE__' + index" :index="index" :value="item.type" @update:value="item.type = $event"></control>
            <control :name="'CALC_FIELDS__' + index" :index="index" :value="item.fields" @update:value="item.fields = $event" :items="calcFieldDropdownOptions[index]" :valueTemplate="valueTemplate(item, index)"></control>
            <div v-if="calcItems.length - 1 === index">
                <control :name="'REMOVE_CALC_ITEM__' + index" :index="index" :actionParams="index"></control>
            </div>
        </div>
        <div class="calc-action-area">
            <control name="ADD_CALC_ITEM"></control>
        </div>
    </div>`,
    controls: [
        {
            name: 'CALC_TYPE',
            type: 'dropdown',
            itemsSource: 'this.calcOptions',
            width: '120px',
            onchange: 'outputCalcItems',
            default: 'add'
        },
        {
            name: 'CALC_FIELDS',
            type: 'multiselect',
            allowCustomValue: true,
            // maximumSelectionLength: 2, // Set by logic
            logic: [
                {
                    record: 'self',
                    valueType: 'value',
                    value: 2,
                    prop: 'maximumSelectionLength',
                    criteria: 'this.component.calcItems[__index__] && this.component.calcItems[__index__].type === "round"'
                },
                {
                    record: 'self',
                    valueType: 'value',
                    value: 'eg: "Value from Calc 1" round by "2" decimal places',
                    prop: 'placeholder',
                    criteria: 'this.component.calcItems[__index__] && this.component.calcItems[__index__].type === "round"'
                }
            ],
            placeholder: 'Choose your field(s) or manually enter any number(s) to calculate.',
            fields: { text: 'text', value: 'value' },
            onchange: 'outputCalcItems',
            default: []
        },
        {
            name: 'ADD_CALC_ITEM',
            type: 'button',
            label: 'Add Row',
            onclick: 'addCalcItem',
            noInputLabel: true,
            helpText: 'Each calculation row groups math calculations, and makes the result available to subsequent rows.'
        },
        {
            name: 'REMOVE_CALC_ITEM',
            type: 'button',
            label: 'Remove',
            onclick: 'removeCalcItem',
        }
    ]
};

const calcEditorCmp = {
    name: "calcEditor",
    editorSettings: editorSettings,
    props: {
        fieldOptions: {
            type: Array,
            default: []
        },
        value: {
            default: []
        }
    },
    data() {
        return {
            calcItems: [],
            calcOptions: [
                'add',
                'subtract',
                'divide',
                'multiply',
                'average', // mean
                'percentage',
                'round'
            ]
        };
    },
    computed: {
        calcFieldDropdownOptions() {
            // Returns an array of arrays of fields, each dropdown list will be paired with the corresponding calcItem
            let output = [];
            let fields = (this.fieldOptions && this.fieldOptions.length) ? this.fieldOptions : []; // this.testCalcFields;
            // Push the fields for first calculation
            output.push(fields);
            // Prepend previous calculation values to fields list for next calculation (& iterate X times)
            if (this.calcItems.length > 1) {
                for (let idx = 1; idx < this.calcItems.length; idx++) {
                    let lastIndex = (idx - 1);
                    let displayIndex = idx; // Show non-zero based indexes in UI
                    let newField = [{
                        text: 'Value from calc ' + displayIndex,
                        value: 'this.prevCalcResults[' + lastIndex + ']',
                        key: (fields.length).toString()
                    }];
                    output.push(newField.concat(fields));
                }
            }
            return output;
        },
        calcResultsPreview() {
            let output = {
                preview: ""
            };
            if (!this.calcItems || !this.calcItems.length) return output;
            let previewString = "";
            let addNumFields = (iCalcItem) => {
                let nums = [];
                for (let ni = 0; ni < iCalcItem.fields.length; ni++) {
                    let iField = iCalcItem.fields[ni];
                    let iFieldOption = this.fieldOptions.find((field) => field.value === iField);
                    if (!iFieldOption && iField.indexOf('prevCalcResults') !== -1) {
                        let displayIndex = Number(iField.replace(/\D/g, '')) + 1;
                        let oText = 'Calc Result ' + displayIndex;
                        iFieldOption = { text: oText };
                    }
                    if (!iFieldOption) {
                        iFieldOption = {
                            text: iField
                        };
                    }
                    nums[ni] = '<span title="' + iFieldOption + '" class="calc-preview-number">' + iFieldOption.text + '</span>';
                }
                if (nums.length) previewString += nums.join(' ' + locals.operators[iCalcItem.type] + ' ');
            };
            for (let ci = 0; ci < this.calcItems.length; ci++) {
                previewString += '<div class="calc-preview-item">';
                let calcDisplayIndex = ci + 1;
                if (ci < this.calcItems.length - 1) previewString += 'Calc ' + calcDisplayIndex + ': ';
                else if (ci === this.calcItems.length - 1) previewString += 'Output: ';
                addNumFields(this.calcItems[ci]);
                previewString += '</div>';
            }
            output = {
                preview: previewString
            };
            return output;
        }
    },
    methods: {
        addCalcItem() {
            // Perform some action
            let newCalcItem = {
                fields: [],
                type: "add"
            };
            this.calcItems.push(newCalcItem);
            this.outputCalcItems();
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },
        removeCalcItem(event, item, index) {
            let tmp = library.cloneObj(this.calcItems);
            tmp.splice(index, 1);
            this.calcItems = tmp;
            this.outputCalcItems();
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },
        valueTemplate(item, index) {
            return library.createTemplateComponent("valueTemplate_" + this.cId + "_" + index,
                {
                    template: `<span>
                                <span class='calc-item-value-field'>{{data.text}}</span>
                                <span class="calc-item-value-operator">{{operator}}</span>
                            </span>`,
                    data() {
                        return {
                            data: {},
                            item: item
                        };
                    },
                    computed: {
                        operator() {
                            return locals.operators[this.item.type];
                        }
                    }
                });
        },
        onLoad() {
            if (this.value) this.calcItems = library.cloneObj(this.value);
        },
        outputCalcItems() {
            this.$emit('update:prop-value', this.calcItems);
        }
    }
};
componentStore.initComponent(calcEditorCmp, calcEditorConfig, '@common/calcEditor.vue');

export default calcEditorCmp;
</script>

<style scoped>
    .calc-preview-pane {
        padding: 10px;
        background: #f7f7f7;
        border-radius: 3px;
        margin-bottom: 10px;
    }
    :deep(.calc-item-value-operator) {
        font-weight: bold;
        background: white;
        border-radius: 50%;
        line-height: 20px;
        text-align: center;
        min-width: 20px;
        height: 20px;
        display: inline-block;
    }
    :deep(.e-chips:last-child .calc-item-value-operator) {
        display: none;
    }
    :deep(.calc-preview-number) {
        display: inline-block;
        padding: 3px;
        background: #c6e1ee;
    }
    .calc-action-area {
        margin: 10px 0 5px 0;
    }
    .calc-action-area, .calc-item-area {
        display: flex;
        flex-direction: row;
    }
    .calc-item-area > div:first-child {
        margin-right: 10px;
    }
    .calc-editor-control {
        border: 1px solid #666;
        padding: 5px;
        border-radius: 4px;
    }
    :deep(.calc-preview-item) {
        padding: 2px 0;
    }
</style>
